import { Component, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, take, tap } from 'rxjs';
import { LanguageCode } from './navigation/menu/components/language-nav-menu/data/laguage-code';
import { ChangeLangEventService } from './navigation/menu/components/language-nav-menu/service/change-lang-event.service';
import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import { NavigationEnd, Router } from '@angular/router';
import { LoginRedirectionService } from './authentication/services/login-redirection.service';
import { AuthenticationService } from './authentication/services/authentication.service';
import {
    ApplicationLock,
    ApplicationLockService,
} from './shared/services/application-lock.service';
import { BrowserCompatibilityCheckService } from './authentication/services/browser-compatibility-check.service';
import { MatDialog } from '@angular/material/dialog';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BrowserCompatibilityNoticeComponent } from './authentication/components/browser-compatibility-notice/browser-compatibility-notice.component';
import { LocalStorageService } from './shared/services/local-storage.service';
import { getContainerWorkspaceSegment } from './shared/models/container-navigation';
import { ContainerType } from './shared/models/container.types';
import { SIDEBAR_NAV_ROUTE_DATA } from './shared/models/sidebar-nav-route-data';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
    private changeLangSubscription: Subscription;
    private activeContextChangedSubscription: Subscription;
    usedLanguageCode: LanguageCode = LanguageCode.DE;
    isLandingPage: boolean = false;
    applicationLock: ApplicationLock;
    isFirstNavigationComplete: boolean = false;

    constructor(
        private translate: TranslateService,
        private changeLangEvent: ChangeLangEventService,
        private router: Router,
        private loginRedirectionService: LoginRedirectionService,
        private authenticationService: AuthenticationService,
        private applicationLockService: ApplicationLockService,
        private browserCompatibilityCheckService: BrowserCompatibilityCheckService,
        private dialog: MatDialog,
        private modal: NgbModal,
        private localStorageService: LocalStorageService
    ) {
        registerLocaleData(localeEn, LanguageCode.EN);
        registerLocaleData(localeDe, LanguageCode.DE);
        translate.addLangs([LanguageCode.DE, LanguageCode.EN]);
        translate.setDefaultLang(LanguageCode.DE);
        translate.use(this.usedLanguageCode);
    }

    ngOnInit(): void {
        this.changeLangSubscription = this.changeLangEvent
            .getChangeLang()
            .subscribe((data) => {
                this.usedLanguageCode = data;
                this.localStorageService.setItem(
                    'change-language',
                    this.usedLanguageCode
                );
                this.translate.use(this.usedLanguageCode);
            });

        this.activeContextChangedSubscription =
            this.authenticationService.activeContextChanged$.subscribe(() => {
                this.modal.dismissAll();
                this.dialog.closeAll();
                this.redirectFromChangedResourceContext();
            });

        this.router.events.subscribe({
            next: (event) => {
                if (event instanceof NavigationEnd) {
                    this.isLandingPage =
                        event.url === '/home' || event.url === '/';
                    this.isFirstNavigationComplete = true;
                }
            },
        });

        this.authenticationService.initializationFinished$
            .pipe(
                take(1),
                tap(() => {
                    this.runCompatibilityChecksAfterLogin();
                    this.redirectIfLoggedIn();
                })
            )
            .subscribe();

        this.applicationLockService.applicationLock.subscribe(
            (applicationLock: ApplicationLock) => {
                this.applicationLock = applicationLock;
            }
        );
    }

    private redirectIfLoggedIn() {
        if (this.authenticationService.isLoggedIn()) {
            const redirectUrl = this.loginRedirectionService.getRedirectUrl();
            if (redirectUrl) {
                this.loginRedirectionService.removeRedirectUrl();
                this.router.navigateByUrl(redirectUrl, {
                    skipLocationChange: false,
                });
            }
        } else {
            this.loginRedirectionService.removeRedirectUrl();
        }
    }

    private redirectFromChangedResourceContext(): void {
        let activeRoute = this.router.routerState.snapshot.root;
        const activeRouteChain = [activeRoute];
        while (activeRoute.firstChild) {
            activeRoute = activeRoute.firstChild;
            activeRouteChain.push(activeRoute);
        }

        const resourceRoute = [...activeRouteChain].reverse().find((route) => {
            const sidebarNav = route.data?.['sidebarNav'];
            return (
                sidebarNav ===
                    SIDEBAR_NAV_ROUTE_DATA.PROJECT_BY_CONTAINER_TYPE ||
                sidebarNav === SIDEBAR_NAV_ROUTE_DATA.DB_BY_CONTAINER_TYPE
            );
        });
        if (!resourceRoute) {
            return;
        }

        const workspaceSegment = getContainerWorkspaceSegment(
            resourceRoute.params?.['containerType'] as ContainerType
        );
        if (workspaceSegment) {
            this.router.navigateByUrl(`/workspace/${workspaceSegment}`);
        }
    }

    private runCompatibilityChecksAfterLogin(): void {
        if (!this.authenticationService.isLoggedIn()) {
            return;
        }

        const isCheckPending =
            this.browserCompatibilityCheckService.isCompatibilityCheckPending();

        if (!isCheckPending) {
            return;
        }

        const checkResult = this.browserCompatibilityCheckService.runChecks();

        if (checkResult.hasWarnings) {
            this.dialog.open(BrowserCompatibilityNoticeComponent, {
                autoFocus: false,
                data: {
                    showBrowserWarning: checkResult.showBrowserWarning,
                    showResolutionWarning: checkResult.showResolutionWarning,
                },
            });
        }

        this.browserCompatibilityCheckService.clearCompatibilityCheckPending();
    }

    ngOnDestroy(): void {
        this.changeLangSubscription.unsubscribe();
        this.activeContextChangedSubscription.unsubscribe();
    }
}
